/**
 * Lightweight ranked matcher for the search index — no external fuzzy lib.
 * Scores each item's label (primary), category, and keywords against the
 * query and returns the best matches, highest score first.
 *
 * Tiers: exact > label prefix > word-start prefix > substring > subsequence.
 * Label matches outweigh category/keyword matches via per-field weights.
 */
import type { SearchItem } from "./searchTypes";

function isSubsequence(needle: string, haystack: string): boolean {
  let i = 0;
  for (let j = 0; j < haystack.length && i < needle.length; j++) {
    if (haystack[j] === needle[i]) i++;
  }
  return i === needle.length;
}

/**
 * Score a single text field against the (already lowercased) query. 0 = no
 * match. Subsequence (the loosest tier) is opt-in: it's fine for short labels
 * but on long free-text (KB excerpts) almost any short query is a subsequence,
 * which floods results with noise — so keywords/categories require a substring.
 */
function fieldScore(q: string, text: string, allowSubsequence = false): number {
  if (!text) return 0;
  const t = text.toLowerCase();
  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  if (t.split(/\s+/).some((w) => w.startsWith(q))) return 65;
  if (t.includes(q)) return 45;
  if (allowSubsequence && q.length >= 3 && isSubsequence(q, t)) return 20;
  return 0;
}

export function scoreSearchItem(query: string, item: SearchItem): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  let best = fieldScore(q, item.label, true); // labels are short → subsequence ok
  best = Math.max(best, fieldScore(q, item.category || "") * 0.5);
  for (const kw of item.keywords || []) {
    best = Math.max(best, fieldScore(q, kw) * 0.6);
  }
  return best;
}

/** The loosest tier (letters in order, not adjacent). Only labels can score it. */
const SUBSEQUENCE_SCORE = 20;

/**
 * Best matches, highest score first, at most `limit`.
 *
 * Help articles never push a real page match off the list. A settings page
 * found by an alias ("coach" -> Roles) scores at most 60, while any article
 * whose TITLE merely starts with the word scores 80. With the list cut to
 * the top N by score alone, a handful of articles removed the one page the
 * person was looking for (found 2026-10-08; the sidebar shows 8 results).
 * So the cut keeps pages, reports and settings first and gives articles the
 * places that are left. A page that only matches loosely (letters in order)
 * is not protected, so it cannot crowd the articles out either.
 */
export function rankSearchItems(
  query: string,
  items: SearchItem[],
  limit = 20
): SearchItem[] {
  const q = query.trim();
  if (!q) return [];
  const scored = items
    .map((item) => ({ item, score: scoreSearchItem(q, item) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.item.label.localeCompare(b.item.label));
  if (scored.length <= limit) return scored.map((x) => x.item);

  const isPageMatch = (x: { item: SearchItem; score: number }) =>
    x.item.group !== "Help" && x.score > SUBSEQUENCE_SCORE;
  const kept = new Set(scored.filter(isPageMatch).slice(0, limit));
  for (const x of scored) {
    if (kept.size >= limit) break;
    kept.add(x);
  }
  // Same order as before: best score first.
  return scored.filter((x) => kept.has(x)).map((x) => x.item);
}
